import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  username: string;
  email?: string;
  phone?: string;
  realName?: string;
  avatar?: string;
  companyName?: string;
  userRole?: string;
}

interface Subscription {
  hasSubscription: boolean;
  subscription?: {
    id: string;
    planName: string;
    packageLevel: number;
    startTime: string;
    endTime: string;
  } | null;
  packageLevel: number;
  daysRemaining?: number;
  planName?: string;
}

interface Quota {
  totalMaps: number;
  usedMaps: number;
  remainingMaps: number;
  totalStorage: number;
  usedStorage: number;
  remainingStorage: number;
  planName: string;
  planCode: string;
  permissions: {
    uploadBasemap: boolean;
    exportMap: boolean;
    publishMap: boolean;
    apiAccess: boolean;
    modelLibrary: boolean;
    teamCollaboration: boolean;
    modelLibraryLimit: number;
    modelLibraryUsed: number;
    teamMemberLimit: number;
  };
}

export interface QuotaPermission {
  uploadBasemap?: boolean;
  exportMap?: boolean;
  publishMap?: boolean;
  apiAccess?: boolean;
  modelLibrary?: boolean;
  teamCollaboration?: boolean;
  modelLibraryLimit?: number;
  modelLibraryUsed?: number;
  teamMemberLimit?: number;
}

export function getFeatureFromQuota(perm: QuotaPermission | undefined, key: string): boolean {
  if (!perm) return false;
  if (key === 'maps') return true;
  if (key === 'basemap') return !!perm.uploadBasemap;
  if (key === 'export') return !!perm.exportMap;
  if (key === 'publish') return !!perm.publishMap;
  if (key === 'models') return !!perm.modelLibrary;
  if (key === 'api') return !!perm.apiAccess;
  if (key === 'team') return !!perm.teamCollaboration;
  return false;
}

export function getCustomModelEnabled(quota: Quota | null): boolean {
  return (quota?.permissions?.modelLibraryLimit ?? 0) > 0;
}

interface AuthState {
  token: string | null;
  user: User | null;
  subscription: Subscription | null;
  quota: Quota | null;
  unreadCount: number;
  setAuth: (token: string, user: User) => void;
  updateUser: (user: Partial<User>) => void;
  updateSubscription: (subscription: Subscription) => void;
  updateQuota: (quota: Quota) => void;
  setUnreadCount: (count: number) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      subscription: null,
      quota: null,
      unreadCount: 0,
      setAuth: (token, user) => set({ token, user }),
      updateUser: (user) => set((state) => ({
        user: state.user ? { ...state.user, ...user } : user as User
      })),
      updateSubscription: (subscription) => set({ subscription }),
      updateQuota: (quota) => set({ quota }),
      setUnreadCount: (count) => set({ unreadCount: count }),
      clearAuth: () => set({ token: null, user: null, subscription: null, quota: null, unreadCount: 0 }),
    }),
    {
      name: 'kimap-user-auth',
    }
  )
);
